/**
 * ListingsPage Component
 *
 * Host's listing management page.
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { functions } from '@/services/firebase';
import { HostLayout } from '../components';
import { useHostListings, ListingStatus } from '../hooks';

type FilterStatus = ListingStatus | 'all';

export const ListingsPage: React.FC = () => {
  const location = useLocation();
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const { listings, isLoading } = useHostListings();

  // Show success message if just published
  const justPublished = location.state?.published;

  // Filter listings
  const filteredListings = React.useMemo(() => {
    let result = listings.filter((l) => !deletedIds.has(l.id));

    if (filter !== 'all') {
      result = result.filter((l) => l.status === filter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (l) =>
          l.title.toLowerCase().includes(query) ||
          l.location.city.toLowerCase().includes(query) ||
          l.location.region.toLowerCase().includes(query)
      );
    }

    return result;
  }, [listings, filter, searchQuery, deletedIds]);

  const statusCounts = React.useMemo(() => {
    return {
      all: listings.length,
      active: listings.filter((l) => l.status === 'active').length,
      paused: listings.filter((l) => l.status === 'paused').length,
      draft: listings.filter((l) => l.status === 'draft').length,
      pending: listings.filter((l) => l.status === 'pending').length,
    };
  }, [listings]);

  return (
    <HostLayout title="My Listings">
      {/* Success Message */}
      {justPublished && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
          <p className="text-green-700">
            Your listing was submitted for review. We&apos;ll be in touch to arrange
            the property visit, then it goes live.
          </p>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Link
            to="/host/listings/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Add Listing
          </Link>
        </div>

        {/* Search */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-64"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {(['all', 'active', 'paused', 'draft'] as FilterStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === status
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            <span className="ml-2 text-sm">({statusCounts[status]})</span>
          </button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border animate-pulse">
              <div className="h-48 bg-gray-200 rounded-t-xl" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="text-center py-12">
          {listings.length === 0 ? (
            <>
              <HomeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No listings yet
              </h3>
              <p className="text-gray-600 mb-6">
                Create your first listing to start hosting guests.
              </p>
              <Link
                to="/host/listings/new"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                <PlusIcon className="w-5 h-5" />
                Create listing
              </Link>
            </>
          ) : (
            <>
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No listings found
              </h3>
              <p className="text-gray-600">
                Try adjusting your filters or search query.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredListings.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              onDeleted={(id) =>
                setDeletedIds((prev) => new Set(prev).add(id))
              }
            />
          ))}
        </div>
      )}
    </HostLayout>
  );
};

// Listing Card Component
interface ListingCardProps {
  listing: {
    id: string;
    title: string;
    location: { city: string; region: string };
    photo: string;
    photos: string[];
    status: ListingStatus;
    stats: {
      rating?: number | null;
    };
    pricing: {
      nightlyRate: number;
      currency: string;
    };
  };
  onDeleted: (id: string) => void;
}

const ListingCard: React.FC<ListingCardProps> = ({ listing, onDeleted }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const photos = listing.photos.length > 0 ? listing.photos : [listing.photo];

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete "${listing.title}"? Listings with upcoming bookings cannot be deleted.`
      )
    )
      return;
    setDeleting(true);
    try {
      const fn = httpsCallable<{ listingId: string }, { success: boolean }>(
        functions,
        'deleteHostListing'
      );
      await fn({ listingId: listing.id });
      toast.success('Listing deleted');
      onDeleted(listing.id);
    } catch (err: any) {
      toast.error(err?.message || 'Could not delete listing');
    } finally {
      setDeleting(false);
    }
  };

  const statusColors: Record<ListingStatus, string> = {
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    draft: 'bg-gray-100 text-gray-600',
    pending: 'bg-orange-100 text-orange-700',
  };

  const goToPrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
      <Link to={`/host/listings/${listing.id}/edit`}>
        <div className="relative h-48 group">
          <img
            src={photos[photoIndex]}
            alt={listing.title}
            className="w-full h-full object-cover"
          />
          {/* Photo navigation arrows */}
          {photos.length > 1 && (
            <>
              <button
                onClick={goToPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white shadow"
              >
                <svg className="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white shadow"
              >
                <svg className="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {/* Dot indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {photos.slice(0, 5).map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      i === photoIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
                {photos.length > 5 && (
                  <span className="text-white text-[8px] leading-none ml-0.5">+{photos.length - 5}</span>
                )}
              </div>
            </>
          )}
          <div className="absolute top-3 left-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                statusColors[listing.status]
              }`}
            >
              {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
            </span>
          </div>
        </div>
      </Link>

      <div className="p-4">
        <Link to={`/host/listings/${listing.id}/edit`}>
          <h3 className="font-semibold text-gray-900 mb-1 hover:text-primary-600">
            {listing.title}
          </h3>
        </Link>
        <p className="text-sm text-gray-500 mb-3">{listing.location.city}, {listing.location.region}</p>

        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-gray-900">
            ${listing.pricing.nightlyRate}
            <span className="font-normal text-gray-500"> / night</span>
          </span>
          {listing.stats.rating && (
            <span className="flex items-center gap-1 text-gray-600">
              <StarIcon className="w-4 h-4 text-yellow-500" />
              {listing.stats.rating.toFixed(1)}
            </span>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mt-4">
          <Link
            to={`/host/listings/${listing.id}/edit`}
            className="flex-1 py-2 text-center text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Edit
          </Link>
          <Link
            to={`/host/calendar?listing=${listing.id}`}
            className="flex-1 py-2 text-center text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Calendar
          </Link>
          <Link
            to={`/listing/${listing.id}`}
            target="_blank"
            className="py-2 px-3 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <ExternalLinkIcon className="w-4 h-4" />
          </Link>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            title="Delete listing"
            className="py-2 px-3 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const ExternalLinkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
  </svg>
);

export default ListingsPage;
